import React, { useState } from 'react';
import { fetchReputation, updateReputation } from '../api/ledger';

export default function Reputation() {
  const [agentId, setAgentId] = useState('');
  const [reputation, setReputation] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleLookup = async () => {
    if (!agentId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchReputation(agentId);
      setReputation(data.reputation);
    } catch (e) {
      setError(String(e) || 'Lookup failed');
      setReputation(null);
    }
    setLoading(false);
  };

  const getScoreColor = (score: number) => {
    if (score >= 0.8) return 'var(--ledger-success)';
    if (score >= 0.5) return 'var(--ledger-warning)';
    return '#ff4757';
  };

  const getScoreLabel = (score: number) => {
    if (score >= 0.9) return 'Excellent';
    if (score >= 0.8) return 'Very Good';
    if (score >= 0.6) return 'Good';
    if (score >= 0.4) return 'Fair';
    return 'Low';
  };

  return (
    <div className="ledger-container">
      <div className="ledger-header">
        <h1>⭐ Agent Reputation</h1>
        <p>Agent별 Reputation 점수를 조회하고 신뢰도를 확인합니다.</p>
      </div>

      <div className="ledger-card">
        <div className="ledger-card-header">
          <span className="ledger-card-title"><span className="ledger-icon">🔍</span> Reputation Lookup</span>
        </div>
        <div style={{ display: 'flex', gap: '12px', marginTop: '16px', flexWrap: 'wrap' }}>
          <input
            className="ledger-input"
            placeholder="Enter Agent ID (e.g., did:agent:001)"
            value={agentId}
            onChange={(e) => setAgentId(e.target.value)}
            style={{ flex: 1, minWidth: '250px' }}
            disabled={loading}
            onKeyDown={(e) => e.key === 'Enter' && handleLookup()}
          />
          <button
            className="ledger-button"
            onClick={handleLookup}
            disabled={loading || !agentId}
            style={{ opacity: loading || !agentId ? 0.5 : 1 }}
          >
            {loading ? '⏳ Looking up...' : '🔍 Lookup'}
          </button>
        </div>

        {error && (
          <div style={{
            padding: '12px',
            marginTop: '16px',
            borderRadius: '8px',
            background: 'rgba(255, 71, 87, 0.1)',
            color: '#ff4757',
            border: '1px solid rgba(255, 71, 87, 0.3)',
            fontSize: '14px',
          }}>
            {error}
          </div>
        )}

        {reputation !== null && (
          <div style={{ marginTop: '24px', textAlign: 'center', padding: '32px 0' }}>
            <div className="ledger-stat-value" style={{ fontSize: '64px', color: getScoreColor(reputation) }}>
              {(reputation * 100).toFixed(1)}
            </div>
            <div className="ledger-stat-label" style={{ fontSize: '14px', marginTop: '8px' }}>
              Reputation Score —{' '}
              <span style={{ color: getScoreColor(reputation), fontWeight: 600 }}>
                {getScoreLabel(reputation)}
              </span>
            </div>
            <div style={{ marginTop: '20px' }}>
              <span className="ledger-hash">{agentId}</span>
            </div>

            <div className="ledger-metrics" style={{ marginTop: '24px', textAlign: 'left' }}>
              <div className="ledger-metric">
                <div className="ledger-metric-value" style={{ color: getScoreColor(reputation) }}>
                  {(reputation * 100).toFixed(0)}%
                </div>
                <div className="ledger-metric-label">Trust Score</div>
              </div>
              <div className="ledger-metric">
                <div className="ledger-metric-value">
                  {reputation >= 0.8 ? '✅' : reputation >= 0.5 ? '⚠️' : '❌'}
                </div>
                <div className="ledger-metric-label">Verification Status</div>
              </div>
              <div className="ledger-metric">
                <div className="ledger-metric-value" style={{ fontSize: '18px' }}>
                  {reputation >= 0.8 ? 'Tier 1' : reputation >= 0.5 ? 'Tier 2' : 'Tier 3'}
                </div>
                <div className="ledger-metric-label">Agent Tier</div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="ledger-grid" style={{ marginTop: '24px' }}>
        <div className="ledger-card">
          <div className="ledger-card-header">
            <span className="ledger-card-title"><span className="ledger-icon">📊</span> Scoring Model</span>
          </div>
          <div className="ledger-code-block">
{`Reputation Score = weighted_avg(
  proof_accuracy     × 0.4,
  verification_rate  × 0.3,
  task_completion    × 0.2,
  community_trust    × 0.1
)

Decay: -0.01/week if inactive
Boost: +0.05 per verified proof`}
          </div>
        </div>
        <div className="ledger-card">
          <div className="ledger-card-header">
            <span className="ledger-card-title"><span className="ledger-icon">🏆</span> Tier System</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span><span className="ledger-badge verified">Tier 1</span></span>
              <span style={{ color: 'var(--ledger-muted)', fontSize: '13px' }}>Score ≥ 80 — Full access, priority matching</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span><span className="ledger-badge pending">Tier 2</span></span>
              <span style={{ color: 'var(--ledger-muted)', fontSize: '13px' }}>Score 50-79 — Standard access</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span><span className="ledger-badge data">Tier 3</span></span>
              <span style={{ color: 'var(--ledger-muted)', fontSize: '13px' }}>Score &lt; 50 — Limited, review required</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
